/**
 * ShopScreen — 商城介面，購買卡包 + 保底進度顯示
 */

import { useEffect, useCallback, useState } from 'react';
import { useShopStore, SR_PITY_MAX, SSR_PITY_MAX, SSR_SOFT_PITY_START } from '../../store/shopStore';
import { useUIStore } from '../../store/uiStore';
import type { PityCounter, CardDefinition, LoginRewardState, LoginRewardResult, DustRates } from '../../services/ShopAPI';
import { PackOpeningOverlay } from './PackOpeningOverlay';

// Rarity color map
const RARITY_COLORS: Record<string, string> = {
  SSR: '#ffd700',
  SR: '#c77dff',
  R: '#4ea8de',
  N: '#aaaaaa',
};

const RARITY_LABELS: Record<string, string> = {
  SSR: '✦ SSR',
  SR: '★ SR',
  R: '◆ R',
  N: '○ N',
};

type ShopTab = 'packs' | 'collection' | 'login';

export function ShopScreen() {
  const { goBack } = useUIStore();
  const {
    packs,
    wallet,
    pityCounters,
    lastPurchase,
    dustRates,
    loginRewardState,
    lastLoginReward,
    isLoadingPacks,
    isPurchasing,
    isDisenchanting,
    isCrafting,
    error,
    fetchPacks,
    fetchWallet,
    fetchPityCounter,
    fetchDustRates,
    fetchLoginReward,
    purchase,
    disenchantCard,
    craftCard,
    disenchantAllDuplicates,
    claimReward,
    clearLastPurchase,
    clearLastLoginReward,
    clearError,
  } = useShopStore();

  const [activeTab, setActiveTab] = useState<ShopTab>('packs');
  const [showAnimation, setShowAnimation] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [showLoginResult, setShowLoginResult] = useState(false);
  const [disenchantMsg, setDisenchantMsg] = useState<string | null>(null);

  // Load data on mount
  useEffect(() => {
    void fetchPacks();
    void fetchWallet();
    void fetchDustRates();
    void fetchLoginReward();
  }, [fetchPacks, fetchWallet, fetchDustRates, fetchLoginReward]);

  // Load pity counters when packs are available
  useEffect(() => {
    for (const pack of packs) {
      void fetchPityCounter(pack.id);
    }
  }, [packs, fetchPityCounter]);

  const handlePurchase = useCallback(async (packId: string) => {
    const result = await purchase(packId);
    if (result) {
      setShowAnimation(true);
    }
  }, [purchase]);

  const handleAnimationComplete = useCallback(() => {
    setShowAnimation(false);
    setShowResult(true);
  }, []);

  const handleCloseResult = useCallback(() => {
    setShowResult(false);
    clearLastPurchase();
    for (const pack of packs) {
      void fetchPityCounter(pack.id);
    }
  }, [clearLastPurchase, packs, fetchPityCounter]);

  const handleDisenchant = useCallback(async (cardCode: string) => {
    const result = await disenchantCard(cardCode);
    if (result) {
      setDisenchantMsg(`分解 ${result.card_code} (${result.rarity}) → +${String(result.dust_gained)} ✧`);
      setTimeout(() => { setDisenchantMsg(null); }, 3000);
      void fetchWallet();
    }
  }, [disenchantCard, fetchWallet]);

  const handleCraft = useCallback(async (cardCode: string) => {
    const result = await craftCard(cardCode);
    if (result) {
      setDisenchantMsg(`合成 ${result.card_code} (${result.rarity}) → -${String(result.dust_spent)} ✧`);
      setTimeout(() => { setDisenchantMsg(null); }, 3000);
      void fetchWallet();
    }
  }, [craftCard, fetchWallet]);

  const handleDisenchantAll = useCallback(async () => {
    const result = await disenchantAllDuplicates();
    if (result) {
      setDisenchantMsg(`批量分解 ${String(result.cards_disenchanted)} 張 → +${String(result.total_dust_gained)} ✧`);
      setTimeout(() => { setDisenchantMsg(null); }, 4000);
      void fetchWallet();
    }
  }, [disenchantAllDuplicates, fetchWallet]);

  const handleClaimReward = useCallback(async () => {
    const result = await claimReward();
    if (result) {
      setShowLoginResult(true);
    }
  }, [claimReward]);

  const handleCloseLoginResult = useCallback(() => {
    setShowLoginResult(false);
    clearLastLoginReward();
    void fetchWallet();
  }, [clearLastLoginReward, fetchWallet]);

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <button style={styles.backButton} onClick={goBack}>← 返回</button>
        <h1 style={styles.title}>🎴 商城</h1>
        <div style={styles.walletDisplay}>
          💰 {wallet ? String(wallet.coins) : '...'} 星幣
          <span style={{ marginLeft: '16px', color: '#88ccff' }}>
            ✧ {wallet ? String(wallet.dust) : '...'} 星塵
          </span>
        </div>
      </div>

      {/* Tabs */}
      <div style={styles.tabBar}>
        <button
          style={{ ...styles.tabButton, ...(activeTab === 'packs' ? styles.tabActive : {}) }}
          onClick={() => { setActiveTab('packs'); }}
        >
          🎴 卡包
        </button>
        <button
          style={{ ...styles.tabButton, ...(activeTab === 'collection' ? styles.tabActive : {}) }}
          onClick={() => { setActiveTab('collection'); }}
        >
          📦 分解 / 合成
        </button>
        <button
          style={{ ...styles.tabButton, ...(activeTab === 'login' ? styles.tabActive : {}) }}
          onClick={() => { setActiveTab('login'); void fetchLoginReward(); }}
        >
          📅 登入獎勵
          {loginRewardState?.claimable && <span style={styles.claimBadge}>!</span>}
        </button>
      </div>

      {/* Error */}
      {error && (
        <div style={styles.errorBar}>
          {error}
          <button style={styles.errorClose} onClick={clearError}>✕</button>
        </div>
      )}

      {/* Disenchant message */}
      {disenchantMsg && (
        <div style={styles.successBar}>{disenchantMsg}</div>
      )}

      {/* Tab Content */}
      {activeTab === 'packs' && (
        <div style={styles.packGrid}>
          {isLoadingPacks ? (
            <div style={styles.loading}>載入中...</div>
          ) : packs.length === 0 ? (
            <div style={styles.empty}>目前沒有可用的卡包</div>
          ) : (
            packs.map((pack) => (
              <PackCard
                key={pack.id}
                name={pack.name}
                description={pack.description}
                price={pack.price}
                cardsPerPack={pack.cards_per_pack}
                pity={pityCounters[pack.id]}
                canAfford={(wallet?.coins ?? 0) >= pack.price}
                isPurchasing={isPurchasing}
                onPurchase={() => { void handlePurchase(pack.id); }}
              />
            ))
          )}
        </div>
      )}

      {activeTab === 'collection' && (
        <CollectionTab
          dustRates={dustRates}
          isDisenchanting={isDisenchanting}
          isCrafting={isCrafting}
          onDisenchant={(code: string) => { void handleDisenchant(code); }}
          onCraft={(code: string) => { void handleCraft(code); }}
          onDisenchantAll={() => { void handleDisenchantAll(); }}
        />
      )}

      {activeTab === 'login' && (
        <LoginRewardTab
          state={loginRewardState}
          onClaim={() => { void handleClaimReward(); }}
        />
      )}

      {/* Pack Opening Animation */}
      {showAnimation && lastPurchase && (
        <PackOpeningOverlay
          cards={lastPurchase.drawn_cards}
          onComplete={handleAnimationComplete}
        />
      )}

      {/* Purchase Result Modal */}
      {showResult && lastPurchase && (
        <PurchaseResultModal
          result={lastPurchase}
          onClose={handleCloseResult}
        />
      )}

      {/* Login Reward Result Modal */}
      {showLoginResult && lastLoginReward && (
        <LoginRewardResultModal
          result={lastLoginReward}
          onClose={handleCloseLoginResult}
        />
      )}
    </div>
  );
}

// --- Collection / Dust Tab ---

function CollectionTab({
  dustRates,
  isDisenchanting,
  isCrafting,
  onDisenchant,
  onCraft,
  onDisenchantAll,
}: {
  dustRates: DustRates | null;
  isDisenchanting: boolean;
  isCrafting: boolean;
  onDisenchant: (cardCode: string) => void;
  onCraft: (cardCode: string) => void;
  onDisenchantAll: () => void;
}) {
  const [craftCode, setCraftCode] = useState('');
  const [disenchantCode, setDisenchantCode] = useState('');

  return (
    <div style={{ padding: '24px', flex: 1, overflowY: 'auto' }}>
      {/* Dust Rates */}
      {dustRates && (
        <div style={{ marginBottom: '24px' }}>
          <h3 style={{ color: '#e8d282', margin: '0 0 12px 0' }}>✧ 星塵兌換表</h3>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            {(['N', 'R', 'SR', 'SSR'] as const).map((rarity) => {
              const rate = dustRates[rarity];
              return (
                <div key={rarity} style={{
                  padding: '12px 20px',
                  backgroundColor: '#252545',
                  borderRadius: '8px',
                  borderLeft: `3px solid ${RARITY_COLORS[rarity] ?? '#555'}`,
                }}>
                  <div style={{ color: RARITY_COLORS[rarity] ?? '#aaa', fontWeight: 'bold', marginBottom: '4px' }}>
                    {RARITY_LABELS[rarity] ?? rarity}
                  </div>
                  <div style={{ fontSize: '12px', color: '#aaa' }}>
                    分解: +{String(rate.disenchant)} ✧
                  </div>
                  <div style={{ fontSize: '12px', color: '#aaa' }}>
                    合成: -{String(rate.craft)} ✧
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Disenchant single card */}
      <div style={{ marginBottom: '24px' }}>
        <h3 style={{ color: '#e8d282', margin: '0 0 12px 0' }}>🔥 分解卡牌</h3>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="輸入卡牌代碼 (card_code)"
            value={disenchantCode}
            onChange={(e) => { setDisenchantCode(e.target.value); }}
            style={{
              padding: '10px 14px',
              fontSize: '14px',
              backgroundColor: '#2a2a4a',
              color: '#ffffff',
              border: '1px solid #3a3a5a',
              borderRadius: '6px',
              outline: 'none',
              width: '250px',
            }}
          />
          <button
            style={{
              ...styles.purchaseButton,
              backgroundColor: '#8b4513',
              padding: '10px 20px',
              fontSize: '14px',
              opacity: isDisenchanting || !disenchantCode.trim() ? 0.5 : 1,
            }}
            onClick={() => {
              if (disenchantCode.trim()) {
                onDisenchant(disenchantCode.trim());
                setDisenchantCode('');
              }
            }}
            disabled={isDisenchanting || !disenchantCode.trim()}
          >
            {isDisenchanting ? '分解中...' : '分解'}
          </button>
        </div>
      </div>

      {/* Bulk Disenchant */}
      <div style={{ marginBottom: '24px' }}>
        <button
          style={{
            ...styles.purchaseButton,
            backgroundColor: '#8b4513',
            padding: '12px 24px',
            fontSize: '14px',
            opacity: isDisenchanting ? 0.5 : 1,
          }}
          onClick={() => { onDisenchantAll(); }}
          disabled={isDisenchanting}
        >
          {isDisenchanting ? '分解中...' : '🔥 一鍵分解所有重複卡'}
        </button>
      </div>

      {/* Craft by card code */}
      <div style={{ marginBottom: '24px' }}>
        <h3 style={{ color: '#e8d282', margin: '0 0 12px 0' }}>🔨 合成卡牌</h3>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="輸入卡牌代碼 (card_code)"
            value={craftCode}
            onChange={(e) => { setCraftCode(e.target.value); }}
            style={{
              padding: '10px 14px',
              fontSize: '14px',
              backgroundColor: '#2a2a4a',
              color: '#ffffff',
              border: '1px solid #3a3a5a',
              borderRadius: '6px',
              outline: 'none',
              width: '250px',
            }}
          />
          <button
            style={{
              ...styles.purchaseButton,
              backgroundColor: '#2e8b57',
              padding: '10px 20px',
              fontSize: '14px',
              opacity: isCrafting || !craftCode.trim() ? 0.5 : 1,
            }}
            onClick={() => {
              if (craftCode.trim()) {
                onCraft(craftCode.trim());
                setCraftCode('');
              }
            }}
            disabled={isCrafting || !craftCode.trim()}
          >
            {isCrafting ? '合成中...' : '合成'}
          </button>
        </div>
        <p style={{ fontSize: '12px', color: '#666', marginTop: '6px' }}>
          輸入卡牌代碼即可用星塵合成卡牌
        </p>
      </div>

      {/* Info */}
      <div style={{ fontSize: '13px', color: '#888', lineHeight: '1.8' }}>
        <p>💡 提示：</p>
        <p>• 分解卡牌可獲得星塵 ✧</p>
        <p>• 在牌組中的卡牌無法分解</p>
        <p>• 一鍵分解會保留每張卡至少 1 張</p>
        <p>• 星塵可用來合成指定卡牌</p>
      </div>
    </div>
  );
}

// --- Login Reward Tab ---

function LoginRewardTab({
  state,
  onClaim,
}: {
  state: LoginRewardState | null;
  onClaim: () => void;
}) {
  if (!state) {
    return <div style={{ padding: '40px', textAlign: 'center', color: '#888' }}>載入中...</div>;
  }

  const schedule = [
    { day: 1, coins: 30, dust: 0, freePack: false },
    { day: 2, coins: 40, dust: 0, freePack: false },
    { day: 3, coins: 50, dust: 10, freePack: false },
    { day: 4, coins: 60, dust: 0, freePack: false },
    { day: 5, coins: 80, dust: 0, freePack: false },
    { day: 6, coins: 100, dust: 20, freePack: false },
    { day: 7, coins: 0, dust: 0, freePack: true },
  ];

  return (
    <div style={{ padding: '24px', flex: 1, overflowY: 'auto' }}>
      <h3 style={{ color: '#e8d282', margin: '0 0 20px 0', fontSize: '20px' }}>📅 每日登入獎勵</h3>

      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '24px' }}>
        {schedule.map((reward) => {
          const isCurrent = reward.day === state.current_day;
          const isPast = reward.day < state.current_day;
          return (
            <div key={reward.day} style={{
              width: '120px',
              padding: '16px',
              backgroundColor: isCurrent ? '#3a3a6a' : '#252545',
              borderRadius: '12px',
              border: isCurrent ? '2px solid #e8d282' : '1px solid #3a3a5a',
              textAlign: 'center',
              opacity: isPast ? 0.5 : 1,
            }}>
              <div style={{
                fontSize: '13px',
                color: isCurrent ? '#e8d282' : '#888',
                marginBottom: '8px',
                fontWeight: isCurrent ? 'bold' : 'normal',
              }}>
                第 {String(reward.day)} 天
              </div>
              <div style={{ fontSize: '20px', marginBottom: '6px' }}>
                {reward.freePack ? '🎴' : '💰'}
              </div>
              <div style={{ fontSize: '12px', color: '#ccc' }}>
                {reward.freePack ? '免費卡包' : `${String(reward.coins)} 星幣`}
              </div>
              {reward.dust > 0 && (
                <div style={{ fontSize: '11px', color: '#88ccff', marginTop: '2px' }}>
                  +{String(reward.dust)} ✧
                </div>
              )}
              {isCurrent && (
                <div style={{ fontSize: '10px', color: '#e8d282', marginTop: '6px' }}>
                  ▼ 今天
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Claim Button */}
      <button
        style={{
          ...styles.purchaseButton,
          backgroundColor: state.claimable ? '#e8d282' : '#555',
          padding: '14px 32px',
          fontSize: '16px',
          cursor: state.claimable ? 'pointer' : 'not-allowed',
          opacity: state.claimable ? 1 : 0.6,
        }}
        onClick={() => { onClaim(); }}
        disabled={!state.claimable}
      >
        {state.claimable ? '🎁 領取今日獎勵' : '✓ 今日已領取'}
      </button>

      {state.last_claim && (
        <p style={{ fontSize: '12px', color: '#666', marginTop: '12px' }}>
          上次領取: {state.last_claim}
        </p>
      )}
    </div>
  );
}

// --- Login Reward Result Modal ---

function LoginRewardResultModal({
  result,
  onClose,
}: {
  result: LoginRewardResult;
  onClose: () => void;
}) {
  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modalContent} onClick={(e) => { e.stopPropagation(); }}>
        <h2 style={styles.modalTitle}>🎁 登入獎勵 - 第 {String(result.day)} 天</h2>

        <div style={{ textAlign: 'center', fontSize: '18px', lineHeight: '2' }}>
          {result.coins_gained > 0 && (
            <div>💰 +{String(result.coins_gained)} 星幣</div>
          )}
          {result.dust_gained > 0 && (
            <div style={{ color: '#88ccff' }}>✧ +{String(result.dust_gained)} 星塵</div>
          )}
          {result.free_pack && (
            <div>🎴 免費卡包！獲得 {String(result.free_pack.drawn_cards.length)} 張卡</div>
          )}
        </div>

        {/* Show drawn cards if free pack */}
        {result.free_pack && (
          <div style={styles.cardResults}>
            {result.free_pack.drawn_cards.map((card, i) => (
              <div
                key={`${card.code}-${String(i)}`}
                style={{
                  ...styles.resultCard,
                  borderColor: RARITY_COLORS[card.rarity] ?? '#555',
                  backgroundColor:
                    card.rarity === 'SSR' ? 'rgba(255, 215, 0, 0.15)' :
                    card.rarity === 'SR' ? 'rgba(199, 125, 255, 0.1)' : '#2a2a4a',
                }}
              >
                <div style={styles.resultRarity}>
                  <span style={{ color: RARITY_COLORS[card.rarity] ?? '#aaa' }}>
                    {RARITY_LABELS[card.rarity] ?? card.rarity}
                  </span>
                </div>
                <div style={styles.resultCardName}>{card.name}</div>
                <div style={styles.resultCardCode}>{card.code}</div>
              </div>
            ))}
          </div>
        )}

        <div style={{ fontSize: '13px', color: '#888', marginTop: '8px' }}>
          💰 {String(result.wallet_coins)} 星幣 | ✧ {String(result.wallet_dust)} 星塵
        </div>

        <button style={styles.closeButton} onClick={onClose}>確認</button>
      </div>
    </div>
  );
}

// --- Pack Card ---

function PackCard({
  name,
  description,
  price,
  cardsPerPack,
  pity,
  canAfford,
  isPurchasing,
  onPurchase,
}: {
  name: string;
  description: string;
  price: number;
  cardsPerPack: number;
  pity: PityCounter | undefined;
  canAfford: boolean;
  isPurchasing: boolean;
  onPurchase: () => void;
}) {
  const srPity = pity?.sr_pity ?? 0;
  const ssrPity = pity?.ssr_pity ?? 0;
  const isInSoftPity = ssrPity >= SSR_SOFT_PITY_START - 1;

  return (
    <div style={styles.packCard}>
      <div style={styles.packHeader}>
        <h3 style={styles.packName}>{name}</h3>
        <span style={styles.packCards}>{String(cardsPerPack)} 張/包</span>
      </div>
      {description && <p style={styles.packDesc}>{description}</p>}

      {/* Pity Progress */}
      <div style={styles.pitySection}>
        <PityBar
          label="SR 保底"
          current={srPity}
          max={SR_PITY_MAX}
          color="#c77dff"
        />
        <PityBar
          label="SSR 保底"
          current={ssrPity}
          max={SSR_PITY_MAX}
          color="#ffd700"
          softPityStart={SSR_SOFT_PITY_START}
        />
        {isInSoftPity && (
          <div style={styles.softPityBadge}>🔥 軟保底中！SSR 機率提升</div>
        )}
        {pity && pity.total_pulls > 0 && (
          <div style={styles.totalPulls}>累計抽數: {String(pity.total_pulls)}</div>
        )}
      </div>

      {/* Purchase Button */}
      <button
        style={{
          ...styles.purchaseButton,
          opacity: canAfford && !isPurchasing ? 1 : 0.5,
          cursor: canAfford && !isPurchasing ? 'pointer' : 'not-allowed',
        }}
        onClick={onPurchase}
        disabled={!canAfford || isPurchasing}
      >
        {isPurchasing ? '抽卡中...' : `💰 ${String(price)} 星幣`}
      </button>
    </div>
  );
}

// --- Pity Progress Bar ---

function PityBar({
  label,
  current,
  max,
  color,
  softPityStart,
}: {
  label: string;
  current: number;
  max: number;
  color: string;
  softPityStart?: number;
}) {
  const progress = Math.min(current / max, 1);
  const softProgress = softPityStart ? (softPityStart - 1) / max : undefined;

  return (
    <div style={styles.pityBar}>
      <div style={styles.pityLabel}>
        <span>{label}</span>
        <span style={{ color }}>{String(current)}/{String(max)}</span>
      </div>
      <div style={styles.pityTrack}>
        {/* Soft pity marker */}
        {softProgress !== undefined && (
          <div
            style={{
              ...styles.softPityMarker,
              left: `${String(softProgress * 100)}%`,
            }}
          />
        )}
        <div
          style={{
            ...styles.pityFill,
            width: `${String(progress * 100)}%`,
            backgroundColor: color,
          }}
        />
      </div>
    </div>
  );
}

// --- Purchase Result Modal ---

function PurchaseResultModal({
  result,
  onClose,
}: {
  result: {
    pack_name: string;
    drawn_cards: CardDefinition[];
    coins_left: number;
    pity?: PityCounter;
  };
  onClose: () => void;
}) {
  // Check if any card was guaranteed by pity
  const hasSSR = result.drawn_cards.some((c) => c.rarity === 'SSR');
  const hasSR = result.drawn_cards.some((c) => c.rarity === 'SR');

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modalContent} onClick={(e) => { e.stopPropagation(); }}>
        <h2 style={styles.modalTitle}>
          {hasSSR ? '🌟 恭喜！獲得 SSR！' : hasSR ? '✨ 抽卡結果' : '🎴 抽卡結果'}
        </h2>

        <div style={styles.cardResults}>
          {result.drawn_cards.map((card, i) => (
            <div
              key={`${card.code}-${String(i)}`}
              style={{
                ...styles.resultCard,
                borderColor: RARITY_COLORS[card.rarity] ?? '#555',
                backgroundColor:
                  card.rarity === 'SSR'
                    ? 'rgba(255, 215, 0, 0.15)'
                    : card.rarity === 'SR'
                    ? 'rgba(199, 125, 255, 0.1)'
                    : '#2a2a4a',
              }}
            >
              <div style={styles.resultRarity}>
                <span style={{ color: RARITY_COLORS[card.rarity] ?? '#aaa' }}>
                  {RARITY_LABELS[card.rarity] ?? card.rarity}
                </span>
              </div>
              <div style={styles.resultCardName}>{card.name}</div>
              <div style={styles.resultCardCode}>{card.code}</div>
            </div>
          ))}
        </div>

        {/* Updated pity info */}
        {result.pity && (
          <div style={styles.resultPity}>
            <span>SR 保底: {String(result.pity.sr_pity)}/{String(SR_PITY_MAX)}</span>
            <span style={{ margin: '0 12px' }}>|</span>
            <span>SSR 保底: {String(result.pity.ssr_pity)}/{String(SSR_PITY_MAX)}</span>
          </div>
        )}

        <button style={styles.closeButton} onClick={onClose}>
          確認
        </button>
      </div>
    </div>
  );
}

// --- Styles ---

const styles: Record<string, React.CSSProperties> = {
  container: {
    width: '100vw',
    height: '100vh',
    backgroundColor: '#1a1a2e',
    display: 'flex',
    flexDirection: 'column',
    fontFamily: 'Arial, sans-serif',
    color: '#ffffff',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    padding: '16px 24px',
    backgroundColor: '#252545',
    borderBottom: '1px solid #3a3a5a',
  },
  backButton: {
    padding: '8px 16px',
    fontSize: '14px',
    backgroundColor: '#3a3a5a',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontFamily: 'Arial, sans-serif',
  },
  title: {
    fontSize: '24px',
    fontWeight: 'bold',
    margin: '0 0 0 24px',
    color: '#e8d282',
  },
  walletDisplay: {
    marginLeft: 'auto',
    fontSize: '18px',
    color: '#e8d282',
    fontWeight: 'bold',
  },
  errorBar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 24px',
    backgroundColor: 'rgba(255, 80, 80, 0.15)',
    color: '#ff6666',
    fontSize: '14px',
  },
  errorClose: {
    background: 'none',
    border: 'none',
    color: '#ff6666',
    cursor: 'pointer',
    fontSize: '16px',
  },
  packGrid: {
    flex: 1,
    display: 'flex',
    flexWrap: 'wrap',
    gap: '24px',
    padding: '24px',
    overflowY: 'auto',
    justifyContent: 'center',
    alignContent: 'flex-start',
  },
  loading: {
    color: '#888888',
    fontSize: '18px',
    textAlign: 'center',
    width: '100%',
    marginTop: '40px',
  },
  empty: {
    color: '#666666',
    fontSize: '16px',
    textAlign: 'center',
    width: '100%',
    marginTop: '40px',
  },
  packCard: {
    width: '320px',
    padding: '20px',
    backgroundColor: '#252545',
    borderRadius: '12px',
    border: '1px solid #3a3a5a',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  packHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  packName: {
    fontSize: '20px',
    fontWeight: 'bold',
    margin: 0,
    color: '#e8d282',
  },
  packCards: {
    fontSize: '13px',
    color: '#888888',
  },
  packDesc: {
    fontSize: '14px',
    color: '#aaaaaa',
    margin: 0,
  },
  pitySection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '12px',
    backgroundColor: '#1e1e3e',
    borderRadius: '8px',
  },
  pityBar: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  pityLabel: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: '12px',
    color: '#cccccc',
  },
  pityTrack: {
    position: 'relative',
    height: '8px',
    backgroundColor: '#3a3a5a',
    borderRadius: '4px',
    overflow: 'hidden',
  },
  pityFill: {
    height: '100%',
    borderRadius: '4px',
    transition: 'width 0.3s ease',
  },
  softPityMarker: {
    position: 'absolute',
    top: 0,
    width: '2px',
    height: '100%',
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    zIndex: 1,
  },
  softPityBadge: {
    fontSize: '11px',
    color: '#ff8844',
    textAlign: 'center',
    fontWeight: 'bold',
  },
  totalPulls: {
    fontSize: '11px',
    color: '#666666',
    textAlign: 'center',
  },
  purchaseButton: {
    padding: '14px',
    fontSize: '18px',
    backgroundColor: '#e8d282',
    color: '#1a1a2e',
    border: 'none',
    borderRadius: '8px',
    fontWeight: 'bold',
    fontFamily: 'Arial, sans-serif',
    marginTop: '4px',
  },
  // Modal
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: '#252545',
    borderRadius: '16px',
    padding: '32px',
    maxWidth: '500px',
    width: '90%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '20px',
  },
  modalTitle: {
    fontSize: '24px',
    fontWeight: 'bold',
    margin: 0,
    color: '#e8d282',
    textAlign: 'center',
  },
  cardResults: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    justifyContent: 'center',
    width: '100%',
  },
  resultCard: {
    width: '120px',
    padding: '12px',
    borderRadius: '8px',
    border: '2px solid',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '6px',
  },
  resultRarity: {
    fontSize: '14px',
    fontWeight: 'bold',
  },
  resultCardName: {
    fontSize: '13px',
    textAlign: 'center',
    fontWeight: 'bold',
  },
  resultCardCode: {
    fontSize: '10px',
    color: '#888888',
  },
  resultPity: {
    fontSize: '13px',
    color: '#888888',
    display: 'flex',
    alignItems: 'center',
  },
  closeButton: {
    padding: '12px 40px',
    fontSize: '16px',
    backgroundColor: '#e8d282',
    color: '#1a1a2e',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontFamily: 'Arial, sans-serif',
  },
  // Tabs
  tabBar: {
    display: 'flex',
    gap: '0',
    backgroundColor: '#252545',
    borderBottom: '1px solid #3a3a5a',
    paddingLeft: '24px',
  },
  tabButton: {
    padding: '12px 24px',
    fontSize: '14px',
    backgroundColor: 'transparent',
    color: '#888',
    border: 'none',
    borderBottom: '2px solid transparent',
    cursor: 'pointer',
    fontFamily: 'Arial, sans-serif',
    fontWeight: 'bold',
    position: 'relative',
  },
  tabActive: {
    color: '#e8d282',
    borderBottomColor: '#e8d282',
  },
  claimBadge: {
    position: 'absolute',
    top: '6px',
    right: '4px',
    width: '16px',
    height: '16px',
    backgroundColor: '#ff4444',
    color: '#fff',
    borderRadius: '50%',
    fontSize: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: 'bold',
  },
  successBar: {
    padding: '12px 24px',
    backgroundColor: 'rgba(80, 200, 80, 0.15)',
    color: '#66ff66',
    fontSize: '14px',
    textAlign: 'center',
  },
};
